import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import useUrlState from "@/hooks/useUrlState";

type MsgType = "SMS" | "LMS" | "MMS";
type SendStatus = "success" | "fail" | "pending" | "scheduled";

interface SendRecord {
  id: string;
  type: MsgType;
  subject?: string;
  message: string;
  hasImage?: boolean;
  recipientCount: number;
  successCount: number;
  failCount: number;
  senderNumber: string;
  status: SendStatus;
  sentAt: string;
  cost: number;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

// 탭 복귀(리로드) 시 로딩 없이 마지막 데이터를 즉시 보여주기 위한 캐시
const ITEMS_CACHE_KEY = "cache:admin_send_stats";

function readItemsCache(): SendRecord[] | null {
  try {
    const raw = sessionStorage.getItem(ITEMS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SendRecord[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeItemsCache(list: SendRecord[]): void {
  try {
    sessionStorage.setItem(ITEMS_CACHE_KEY, JSON.stringify(list));
  } catch {
    // 캐시 저장 실패는 무시
  }
}

const TYPE_OPTIONS: Array<"전체" | MsgType> = ["전체", "SMS", "LMS", "MMS"];
const STATUS_OPTIONS: Array<"전체" | SendStatus> = ["전체", "success", "fail", "pending", "scheduled"];

const TYPE_STYLE: Record<MsgType, { bg: string; text: string }> = {
  SMS: { bg: "bg-violet-500/20", text: "text-violet-300" },
  LMS: { bg: "bg-pink-500/20", text: "text-pink-300" },
  MMS: { bg: "bg-amber-500/20", text: "text-amber-300" },
};

const STATUS_STYLE: Record<SendStatus, { bg: string; border: string; text: string; label: string }> = {
  success: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400", label: "성공" },
  fail: { bg: "bg-red-500/10", border: "border-red-500/20", text: "text-red-400", label: "실패" },
  pending: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400", label: "발송 중" },
  scheduled: { bg: "bg-violet-500/10", border: "border-violet-500/20", text: "text-violet-400", label: "예약" },
};

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function SendStatsTab() {
  const { session } = useAuth();
  const cachedItemsRef = useRef<SendRecord[] | null>(readItemsCache());
  const [items, setItems] = useState<SendRecord[]>(cachedItemsRef.current ?? []);
  const [loading, setLoading] = useState(cachedItemsRef.current === null);
  const [error, setError] = useState<string | null>(null);
  // 필터 상태를 주소(URL)에도 남겨 새로고침·링크 공유에도 유지 (sessionStorage 백업)
  const [search, setSearch] = useUrlState("stat_q", "");
  const [typeFilter, setTypeFilter] = useUrlState("stat_type", "전체");
  const [statusFilter, setStatusFilter] = useUrlState("stat_status", "전체");
  const [pageRaw, setPageRaw] = useUrlState("stat_page", "1");
  const page = Math.max(1, parseInt(pageRaw, 10) || 1);
  const setPage = (next: number | ((prev: number) => number)) => {
    const target = typeof next === "function" ? next(page) : next;
    setPageRaw(String(Math.max(1, target)));
  };
  const PER_PAGE = 10;

  const fetchData = useCallback(async () => {
    const accessToken = session?.access_token;
    if (!accessToken) return;
    // 캐시된 데이터가 이미 있으면 로딩 화면 없이 조용히 갱신
    if (cachedItemsRef.current === null) setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/get-admin-send-history?user_id=`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || `HTTP ${res.status}`);
      }

      const json = await res.json();
      const raw = (json.send_history ?? []) as any[];

      const mapped: SendRecord[] = raw.map((h) => {
        const rawType = (h.type || "SMS").toUpperCase() as MsgType;
        const rawStatus = (h.status || "success") as SendStatus;
        const successCount = h.success_count ?? 0;
        const failCount = h.fail_count ?? 0;
        const recipientCount = h.recipient_count ?? (successCount + failCount);
        return {
          id: String(h.id),
          type: ["SMS", "LMS", "MMS"].includes(rawType) ? rawType : "SMS",
          subject: h.subject || undefined,
          message: h.message || "",
          hasImage: !!h.has_image || rawType === "MMS",
          recipientCount,
          successCount,
          failCount,
          senderNumber: h.sender_number || h.sender_id || "-",
          status: ["success", "fail", "pending", "scheduled"].includes(rawStatus) ? rawStatus : "success",
          sentAt: h.sent_at || h.created_at || "",
          cost: h.total_cost ?? h.cost ?? 0,
        };
      });

      setItems(mapped);
      writeItemsCache(mapped);
    } catch (err: any) {
      console.error("[SendStats] fetch error:", err);
      setError(err.message || "발송 통계를 불러오는 중 오류가 발생했습니다.");
    } finally {
      cachedItemsRef.current = cachedItemsRef.current ?? [];
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    let result = [...items];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (r) =>
          (r.message?.toLowerCase() ?? "").includes(q) ||
          (r.subject?.toLowerCase() ?? "").includes(q) ||
          r.senderNumber.includes(q)
      );
    }
    if (typeFilter !== "전체") {
      result = result.filter((r) => r.type === typeFilter);
    }
    if (statusFilter !== "전체") {
      result = result.filter((r) => r.status === statusFilter);
    }
    return result;
  }, [items, search, typeFilter, statusFilter]);

  // 필터가 실제로 바뀌면 첫 페이지로 (최초 복원 시에는 페이지 유지)
  const skipPageResetRef = useRef(true);
  useEffect(() => {
    if (skipPageResetRef.current) {
      skipPageResetRef.current = false;
      return;
    }
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, typeFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const paged = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  // Aggregates
  const totalRecipients = items.reduce((s, h) => s + h.recipientCount, 0);
  const totalSuccess = items.reduce((s, h) => s + h.successCount, 0);
  const totalFail = items.reduce((s, h) => s + h.failCount, 0);
  const totalCost = items.reduce((s, h) => s + h.cost, 0);
  const successRate = totalRecipients > 0 ? Math.round((totalSuccess / totalRecipients) * 100) : 0;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-rose-500/40 border-t-rose-500 rounded-full animate-spin mb-3"></div>
        <p className="text-gray-500 text-sm">발송 통계 불러오는 중...</p>
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <i className="ri-error-warning-line text-3xl text-red-400 mb-2 block"></i>
        <p className="text-red-400 text-sm mb-3">{error}</p>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-white/[0.06] hover:bg-white/[0.1] text-gray-300 rounded-lg text-sm cursor-pointer transition-colors"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4">
          <p className="text-gray-500 text-[11px] font-semibold uppercase">총 발송 건수</p>
          <p className="text-white text-lg font-bold mt-1">{items.length.toLocaleString()}건</p>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4">
          <p className="text-gray-500 text-[11px] font-semibold uppercase">총 수신자</p>
          <p className="text-white text-lg font-bold mt-1">{totalRecipients.toLocaleString()}명</p>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4">
          <p className="text-gray-500 text-[11px] font-semibold uppercase">성공률</p>
          <p className={`text-lg font-bold mt-1 ${successRate >= 95 ? "text-emerald-400" : successRate >= 80 ? "text-amber-400" : "text-red-400"}`}>
            {successRate}%
          </p>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4">
          <p className="text-gray-500 text-[11px] font-semibold uppercase">사용 크레딧</p>
          <p className="text-amber-400 text-lg font-bold mt-1">₩{totalCost.toLocaleString()}</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="메시지, 발신번호 검색"
            className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-rose-500/40 transition-colors"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center bg-white/[0.04] border border-white/[0.08] rounded-lg p-0.5 gap-0.5">
            {TYPE_OPTIONS.map((t) => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                  typeFilter === t ? "bg-rose-600 text-white" : "text-gray-400 hover:text-white"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="flex items-center bg-white/[0.04] border border-white/[0.08] rounded-lg p-0.5 gap-0.5">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                  statusFilter === s ? "bg-rose-600 text-white" : "text-gray-400 hover:text-white"
                }`}
              >
                {STATUS_STYLE[s]?.label || "전체"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/[0.08]">
                {["유형", "발송일", "발신번호", "수신자", "성공/실패", "상태", "비용"].map((h) => (
                  <th key={h} className="px-4 py-3 text-gray-500 text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {paged.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-500 text-sm">
                    조건에 맞는 발송 기록이 없습니다
                  </td>
                </tr>
              ) : (
                paged.map((r) => {
                  const typeSt = TYPE_STYLE[r.type];
                  const statusSt = STATUS_STYLE[r.status];
                  const rate = r.recipientCount > 0 ? Math.round((r.successCount / r.recipientCount) * 100) : 0;
                  return (
                    <tr key={r.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${typeSt.bg} ${typeSt.text}`}>
                            {r.type}
                          </span>
                          {r.subject && <span className="text-white text-xs truncate max-w-[120px]">{r.subject}</span>}
                        </div>
                        <p className="text-gray-600 text-[11px] truncate max-w-[200px] mt-0.5">{r.message}</p>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">
                        {formatDateTime(r.sentAt)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-400 text-xs">
                        {r.senderNumber}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-white text-sm">
                        {r.recipientCount.toLocaleString()}명
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-emerald-400">{r.successCount.toLocaleString()}</span>
                          <span className="text-gray-600">/</span>
                          <span className="text-red-400">{r.failCount.toLocaleString()}</span>
                          <span className={`text-[11px] font-semibold ${rate >= 95 ? "text-emerald-400" : rate >= 80 ? "text-amber-400" : "text-red-400"}`}>
                            ({rate}%)
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${statusSt.bg} ${statusSt.border} ${statusSt.text}`}>
                          {statusSt.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-white text-sm font-medium">
                        {r.cost > 0 ? `₩${r.cost.toLocaleString()}` : "—"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (() => {
          const PAGE_WINDOW = 10;
          const currentWindow = Math.ceil(safePage / PAGE_WINDOW);
          const windowStart = (currentWindow - 1) * PAGE_WINDOW + 1;
          const windowEnd = Math.min(currentWindow * PAGE_WINDOW, totalPages);
          const hasPrevWindow = windowStart > 1;
          const hasNextWindow = windowEnd < totalPages;
          const pagesInWindow = Array.from({ length: windowEnd - windowStart + 1 }, (_, i) => windowStart + i);

          return (
          <div className="flex items-center justify-center gap-1 py-3 border-t border-white/[0.08]">
            <button
              onClick={() => setPage(1)}
              disabled={safePage === 1}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
            >
              <i className="ri-skip-left-line text-sm"></i>
            </button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-s-line text-sm"></i>
            </button>

            {hasPrevWindow && (
              <button
                onClick={() => setPage(windowStart - 1)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-xs text-gray-500 hover:text-white hover:bg-white/[0.05] cursor-pointer transition-colors"
              >
                ...
              </button>
            )}

            {pagesInWindow.map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  p === safePage
                    ? "bg-rose-500/20 border border-rose-500/30 text-rose-400"
                    : "text-gray-500 hover:text-white hover:bg-white/[0.05]"
                }`}
              >
                {p}
              </button>
            ))}

            {hasNextWindow && (
              <button
                onClick={() => setPage(windowEnd + 1)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-xs text-gray-500 hover:text-white hover:bg-white/[0.05] cursor-pointer transition-colors"
              >
                ...
              </button>
            )}

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-right-s-line text-sm"></i>
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={safePage === totalPages}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white disabled:opacity-30 cursor-pointer transition-colors"
            >
              <i className="ri-skip-right-line text-sm"></i>
            </button>
          </div>
          );
        })()}
      </div>
    </div>
  );
}